import { EventStatus } from "@satyrsmc/shared/lib/enums";
import { isEventPast as isEventDatePast } from "@satyrsmc/shared/lib/date-utils";

export type EventRegistrationBlock =
  { blocked: true; reason: string; variant: "error" | "muted" } | { blocked: false };

export function getEventRegistrationBlock(event: {
  status: EventStatus;
  start_date: string | null;
  end_date: string | null;
}): EventRegistrationBlock {
  if (event.status === EventStatus.Cancelled) {
    return { blocked: true, reason: "This event has been cancelled.", variant: "error" };
  }
  // A multi-day event stays open through its end_date; a single-day event
  // (no end_date) closes once its own start_date has passed. The date check
  // outranks the draft one: a past-dated draft (visible to admins only) is
  // "closed", not "not yet open".
  const referenceDate = event.end_date ?? event.start_date;
  if (referenceDate && new Date(referenceDate) < new Date()) {
    return { blocked: true, reason: "Registration for this event is closed.", variant: "muted" };
  }
  if (event.status === EventStatus.Draft) {
    return {
      blocked: true,
      reason: "This event is not yet open for registration.",
      variant: "muted",
    };
  }
  return { blocked: false };
}

/**
 * True once an event has happened and is over — i.e. its last day is fully
 * behind us. Cancelled and draft events are never "past": they didn't take
 * place, so past-tense UI (attendance, photos) doesn't apply to them.
 * Day-based via the shared {@link isEventDatePast}, unlike the instant-based
 * {@link getEventRegistrationBlock}, so an event stays not-past through the
 * end of its final day.
 */
export function isEventPast(event: {
  status: EventStatus;
  start_date: string | null;
  end_date: string | null;
}): boolean {
  if (event.status === EventStatus.Cancelled || event.status === EventStatus.Draft) return false;
  return isEventDatePast(event.start_date, event.end_date);
}

/**
 * True once a refund cutoff date has passed — compared by calendar date
 * (UTC), not time-of-day, so refunds are still allowed through the end of
 * the cutoff day itself. Mirrors `isPastRefundCutoff` in the API's
 * `lib/date.ts`; duplicated here since the frontend doesn't depend on
 * `packages/api`.
 */
export function isPastRefundCutoff(
  cutoffDate: string | null | undefined,
  now: Date = new Date(),
): boolean {
  if (!cutoffDate) return false;
  const cutoff = new Date(cutoffDate);
  if (Number.isNaN(cutoff.getTime())) return false;
  const today = Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate());
  const cutoffDay = Date.UTC(cutoff.getUTCFullYear(), cutoff.getUTCMonth(), cutoff.getUTCDate());
  return today > cutoffDay;
}

/**
 * The single "upcoming" event out of a list — published (not draft), not
 * cancelled, not yet ended (reuses the same open-for-registration predicate
 * as getEventRegistrationBlock), and with a real start_date to be soonest-
 * sorted by (a dateless event can't meaningfully be "upcoming"). Returns
 * undefined if nothing qualifies.
 */
export function findUpcomingEvent<
  T extends { status?: EventStatus; end_date: string | null; start_date: string | null },
>(events: T[]): T | undefined {
  return events
    .filter((e) => e.start_date !== null)
    .filter(
      (e) =>
        !getEventRegistrationBlock({
          status: e.status ?? EventStatus.Draft,
          start_date: e.start_date,
          end_date: e.end_date,
        }).blocked,
    )
    .sort((a, b) => a.start_date!.localeCompare(b.start_date!))[0];
}
